import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmail } from 'validator';
import {
  Modal,
  Box,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
} from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ChevronDown, X } from 'lucide-react';
import moment from 'moment-timezone';
import { ChipInput } from '../../components/muiCustomized/ChipInput';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import { timeToUTCDateObject, convertUtcTimeToTimezone } from '../../utils/time';
import API from '../../api/endpoints';
import { sendRequest } from '../../api/shootAPI';

const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
// Matches the legacy time picker, which only allowed 10-minute steps
const MINUTE_STEP = 10;
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => String(i * MINUTE_STEP).padStart(2, '0'));

function to12(h24) {
  const n = parseInt(h24, 10);
  const period = n >= 12 ? 'PM' : 'AM';
  const h = n % 12 === 0 ? 12 : n % 12;
  return { h: String(h).padStart(2, '0'), period };
}

function to24(h12, period) {
  let n = parseInt(h12, 10);
  if (period === 'AM' && n === 12) n = 0;
  else if (period === 'PM' && n !== 12) n += 12;
  return String(n).padStart(2, '0');
}

const selectClassName = 'w-full appearance-none pl-3 pr-9 py-2 border border-rule rounded-[8px] '
  + 'text-sm bg-white text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 '
  + 'focus:border-brand-500 transition';

const inputClassName = 'w-full px-3 py-2 border border-rule rounded-[8px] text-sm bg-white '
  + 'text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 '
  + 'focus:ring-brand-500/20 focus:border-brand-500 transition';

function Chevron() {
  return (
    <ChevronDown
      size={14}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none"
    />
  );
}

// MUI selects restyled to match the prototype's bordered selects
const muiSelectSx = {
  borderRadius: '8px',
  fontSize: 14,
  backgroundColor: '#fff',
  color: '#0e1e40',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e1e6' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3d5a8a' },
  '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(61, 90, 138, 0.2)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3d5a8a', borderWidth: '1px' },
  '& .MuiSelect-icon': { color: '#546077' },
};

// Dropdown panel styled like the other redesigned dropdowns
const menuProps = {
  PaperProps: {
    sx: {
      marginTop: '4px',
      border: '1px solid #e0e1e6',
      borderRadius: '8px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
      '& .MuiList-root': { padding: '4px 0' },
    },
  },
};

// Two-line timezone options: small bold heading + the timezone value below.
// Selected = light-blue highlight like the other single-select dropdowns.
const tzMenuItemSx = {
  display: 'block',
  padding: '8px 12px',
  borderRadius: '4px',
  margin: '0 4px',
  '&:hover': { backgroundColor: '#fbfbfc' },
  '&.Mui-selected': {
    backgroundColor: '#e8eef7',
    '&:hover': { backgroundColor: '#e8eef7' },
    '& p': { color: '#213d69' },
  },
};

// Multi-select options: selected entries muted, like ChipSelectSearch's panel
const freqMenuItemSx = {
  fontSize: 14,
  fontWeight: 500,
  color: '#0e1e40',
  padding: '8px 12px',
  '&:hover': { backgroundColor: '#fbfbfc' },
  '&.Mui-selected': {
    color: '#546077',
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: '#fbfbfc' },
  },
};

// Chips inside the frequency field — same look as the other multiselect inputs
const freqChipSx = {
  backgroundColor: '#e8eef7',
  color: '#213d69',
  fontWeight: 500,
  fontSize: 12,
  borderRadius: '4px',
  height: 22,
  '& .MuiChip-label': { paddingLeft: '8px', paddingRight: '4px' },
  '& .MuiChip-deleteIcon': {
    color: '#213d69',
    margin: '0 4px 0 0',
    borderRadius: '2px',
    '&:hover': { color: '#213d69', backgroundColor: 'rgba(61, 90, 138, 0.1)' },
  },
};

function TimezoneSelectField({
  id, value, onChange, options,
}) {
  return (
    <Select
      id={id}
      fullWidth
      size="small"
      value={value}
      onChange={onChange}
      sx={muiSelectSx}
      MenuProps={menuProps}
      renderValue={(selected) => selected}
    >
      {options.map(({ heading, description, value: optionValue }) => (
        <MenuItem value={optionValue} key={`${heading}-${optionValue}`} sx={tzMenuItemSx}>
          <p className="text-xs font-semibold text-ink-900 mb-0.5">{heading}</p>
          <p className="text-sm text-ink-500 mb-0">{description}</p>
        </MenuItem>
      ))}
    </Select>
  );
}

TimezoneSelectField.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    heading: PropTypes.string,
    description: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
};

function ScheduleEmailModal({
  closeModal,
  teamId,
  teamName,
  timezoneOptions,
}) {
  const { user } = useContext(UserContext);
  const [id, setId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState([user.email]);
  const [subject, setSubject] = useState('Team Engagement Dashboard Daily Report');
  const [body, setBody] = useState('Please download the attached Team Engagement Dashboard Daily Report');
  // Prefilled like the prototype (16:40) so the form is submittable without
  // touching the time selects; a saved schedule's time overrides it on load.
  const [deliveryTime, setDeliveryTime] = useState(timeToUTCDateObject('16:40'));
  const [deliveryTimezone, setDeliveryTimezone] = useState(user.timezone || 'Etc/UTC');
  const [reportTimezone, setReportTimezone] = useState('Members timezone');
  const [emailError, setEmailError] = useState('');
  const [frequency, setFrequency] = useState([]);
  const { displayNotification } = useContext(NotificationContext);

  const fetchData = async () => {
    setIsLoading(true);
    const res = await sendRequest({
      method: 'get',
      url: API.TEAM.emailSchedule(teamId),
    });

    if (res.success) {
      const { emailSchedule } = res.data;
      if (emailSchedule) {
        setId(emailSchedule.id);
        setEmailAddresses(emailSchedule.emailAddresses.split(','));
        setSubject(emailSchedule.subject);
        setBody(emailSchedule.body);
        setDeliveryTime(timeToUTCDateObject(emailSchedule.deliveryTime));
        setDeliveryTimezone(emailSchedule.deliveryTimezone || user.timezone || 'Etc/UTC');
        setReportTimezone(emailSchedule.reportTimezone || 'Members timezone');
        setFrequency(emailSchedule.frequency ? JSON.parse(emailSchedule.frequency) : []);
      }
    } else {
      displayNotification({
        message: res.data.error,
        severity: 'error',
      });
    }
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    setIsUpdating(true);
    const formData = {
      emailAddresses: emailAddresses.toString(),
      subject,
      body,
      deliveryTime: moment(deliveryTime).format('HH:mm'),
      deliveryTimezone,
      reportTimezone,
      frequency: JSON.stringify(frequency),
    };

    const method = id ? 'put' : 'post';
    const res = await sendRequest({
      method,
      url: API.TEAM.emailSchedule(teamId),
      data: formData,
    });

    if (res.success) {
      setTimeout(() => {
        closeModal();
        displayNotification({
          message: `Email schedule saved for ${teamName ? `“${teamName}”` : 'Team Engagement'}.`,
          severity: 'success',
          autoHideDuration: 3000,
        });
      }, 500);
    } else {
      displayNotification({
        message: 'There was an issue scheduling email.',
        severity: 'error',
      });
    }
    setIsUpdating(false);
  };

  const validateEmails = (emails) => {
    const invalidEmails = emails.filter((email) => !isEmail(email));
    if (invalidEmails.length > 0) {
      const validEmails = emails.filter((email) => isEmail(email));
      setEmailAddresses(validEmails);
      setEmailError(`Invalid email: ${invalidEmails.join(', ')}`);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    validateEmails(emailAddresses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailAddresses]);

  // 12h parts for the time selects; defaults shown until the user picks
  const hhmm = deliveryTime ? moment(deliveryTime).format('HH:mm') : '00:00';
  const [h24Raw, mRaw] = hhmm.split(':');
  const { h: h12, period } = to12(h24Raw);
  const minute = MINUTES.includes(mRaw) ? mRaw : MINUTES[0];

  const updateTime = (nh12, nm, nperiod) => {
    setDeliveryTime(timeToUTCDateObject(`${to24(nh12, nperiod)}:${nm}`));
  };

  const displayTime = deliveryTime
    ? convertUtcTimeToTimezone(moment(deliveryTime)
      .format('HH:mm'), deliveryTimezone === 'Members timezone' ? user.timezone : deliveryTimezone)
    : '';

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c create-team-modal">
      <Box style={{
        width: 448,
        padding: 24,
        background: '#fff',
        borderRadius: 8,
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative',
      }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={closeModal}
          className={'absolute top-4 right-4 bg-transparent border-0 p-0 text-ink-500 '
            + 'opacity-70 hover:opacity-100 transition-opacity'}
        >
          <X size={16} />
        </button>
        <h5 className="text-lg font-semibold text-ink-900 mb-4">Schedule Email</h5>
        {isLoading && (
          <div className="position-relative" style={{ minHeight: 200 }}>
            <div className="edit-modal loader-container">
              <CircularProgress />
            </div>
          </div>
        )}
        {!isLoading && (
          <div className="space-y-4">
            <ChipInput
              id="ChipInput"
              label="Email Addresses"
              placeholder="Add email"
              helperText={emailError || 'Enter email addresses, separated by commas'}
              values={emailAddresses}
              setValues={setEmailAddresses}
              error={!!emailError}
            />
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="email-subject" className="block text-sm font-medium text-ink-900 mb-1.5">
                Subject
              </label>
              <input
                id="email-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className={inputClassName}
              />
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="email-body" className="block text-sm font-medium text-ink-900 mb-1.5">
                Email Body
              </label>
              <textarea
                id="email-body"
                rows={3}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className={`${inputClassName} resize-y`}
              />
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="email-time-hour" className="block text-sm font-medium text-ink-900 mb-1.5">
                Delivery Time (UTC)
              </label>
              <div className="grid grid-cols-[1fr_auto_1fr_1fr] items-center gap-2">
                <div className="relative">
                  <select
                    id="email-time-hour"
                    aria-label="Hour"
                    value={h12}
                    onChange={(e) => updateTime(e.target.value, minute, period)}
                    className={selectClassName}
                  >
                    {HOURS_12.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <Chevron />
                </div>
                <span className="text-ink-500 font-semibold text-center">:</span>
                <div className="relative">
                  <select
                    aria-label="Minute"
                    value={minute}
                    onChange={(e) => updateTime(h12, e.target.value, period)}
                    className={selectClassName}
                  >
                    {MINUTES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <Chevron />
                </div>
                <div className="relative">
                  <select
                    aria-label="AM or PM"
                    value={period}
                    onChange={(e) => updateTime(h12, minute, e.target.value)}
                    className={selectClassName}
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                  <Chevron />
                </div>
              </div>
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="delivery-tz" className="block text-sm font-medium text-ink-900 mb-1.5">
                {`Delivery Timezone ${displayTime}`.trim()}
              </label>
              <TimezoneSelectField
                id="delivery-tz"
                value={deliveryTimezone}
                onChange={(e) => setDeliveryTimezone(e.target.value)}
                options={timezoneOptions}
              />
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="report-tz" className="block text-sm font-medium text-ink-900 mb-1.5">
                Member&apos;s Report Timezone
              </label>
              <TimezoneSelectField
                id="report-tz"
                value={reportTimezone}
                onChange={(e) => setReportTimezone(e.target.value)}
                options={timezoneOptions}
              />
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="frequency" className="block text-sm font-medium text-ink-900 mb-1.5">
                Frequency
              </label>
              <Select
                id="frequency"
                multiple
                fullWidth
                displayEmpty
                size="small"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                sx={muiSelectSx}
                MenuProps={menuProps}
                renderValue={(selected) => (selected.length === 0 ? (
                  <span className="text-ink-500">Select</span>
                ) : (
                  <span className="flex flex-wrap items-center gap-1">
                    {selected.map((item) => (
                      <Chip
                        key={item}
                        label={item.charAt(0).toUpperCase() + item.slice(1)}
                        size="small"
                        sx={freqChipSx}
                        deleteIcon={(
                          <X
                            size={11}
                            // Keep the X click from toggling the dropdown open
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        )}
                        onDelete={() => setFrequency(frequency.filter((f) => f !== item))}
                      />
                    ))}
                  </span>
                ))}
              >
                <MenuItem value="daily" sx={freqMenuItemSx}>Daily</MenuItem>
                <MenuItem value="weekly" sx={freqMenuItemSx}>Weekly</MenuItem>
                <MenuItem value="monthly" sx={freqMenuItemSx}>Monthly</MenuItem>
              </Select>
            </div>
          </div>
        )}
        <div className="flex items-center justify-start gap-2 mt-4">
          <button
            type="button"
            onClick={closeModal}
            className={'inline-flex items-center justify-center px-4 py-2 border border-rule '
              + 'rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition'}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isUpdating || !(emailAddresses.length && subject && body && deliveryTime)}
            className={'inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white '
              + 'rounded-[8px] text-sm font-medium hover:bg-[#458a44] disabled:bg-[#53A551]/40 '
              + 'disabled:cursor-not-allowed transition'}
          >
            {isUpdating ? 'Saving...' : 'Save Schedule'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

ScheduleEmailModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  teamId: PropTypes.string.isRequired,
  teamName: PropTypes.string,
  timezoneOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
};

export default ScheduleEmailModal;
